// src/lib/http.ts
import axios, {
  AxiosInstance,
  AxiosRequestHeaders,
  AxiosResponse,
  AxiosResponseHeaders,
} from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import * as Sentry from '@sentry/react';

export interface JsonResponse<T> {
  data: T;
  status: number;
  headers: AxiosResponseHeaders;
}

const API_URL = import.meta.env.VITE_API_BASE_URL;

const client: AxiosInstance = axios.create({ baseURL: API_URL });

client.interceptors.request.use(async (cfg) => {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();

  if (!token) {
    throw new Error('Missing auth token for API request');
  }

  cfg.headers = {
    ...(cfg.headers || {}),
    Authorization: token,
    'Content-Type': 'application/json',
  } as AxiosRequestHeaders;
  return cfg;
});

/**
 * Centralized request handler: wraps an Axios call,
 * captures 404 as null, logs errors to Sentry, rethrows otherwise.
 */
async function handleRequest<T>(
  fn: () => Promise<AxiosResponse<T>>
): Promise<T | null> {
  try {
    const resp = await fn();
    return resp.data as T;
  } catch (err: any) {
    if (err.response?.status === 404) {
      return null;
    }
    Sentry.captureException(err);
    throw err;
  }
}

/**
 * GET → JsonResponse<T> | null
 */
export function getRequest<T>(
  _apiName: string,
  path: string
): Promise<T | null> {
  return handleRequest(() => client.get<T>(path));
}

/**
 * POST → JsonResponse<T> | null
 */
export function postRequest<T, B = unknown>(
  _apiName: string,
  path: string,
  body: B
): Promise<T | null> {
  return handleRequest(() => client.post<T>(path, body));
}

/**
 * PUT → JsonResponse<T> | null
 */
export function putRequest<T, B = unknown>(
  _apiName: string,
  path: string,
  body: B
): Promise<T | null> {
  return handleRequest(() => client.put<T>(path, body));
}

/**
 * DELETE → JsonResponse<T> | null
 */
export function deleteRequest<T, B = unknown>(
  _apiName: string,
  path: string,
  body?: B
): Promise<T | null> {
  return handleRequest(() => client.delete<T>(path, { data: body }));
}
